<template>
  <div class="pt-0">
    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!teasers.length">
      <component
        :is="isFreeToPlayTeaser(teaser) ? FreeToPlayTeaser : ScratchcardTeaser"
        v-for="(teaser, index) in teasers"
        :key="isFreeToPlayTeaser(teaser) ? 'free-to-play-scratchcard' : teaser.slug"
        v-bind="teaser"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        :free-to-play="isFreeToPlayTeaser(teaser)"
        @click="handleTeaserClick(teaser)"
      />

      <template #no-results>
        <LazyNoResults
          :headline="$t('playnow.tab.scratchcards.noresults')"
          illustration="sold-out"
          variant="outlined"
        />
      </template>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import type {
  FreeToPlayTeaserPayload,
  ScratchcardTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import FreeToPlayTeaser from '#free-to-play/app/components/FreeToPlayTeaser.vue'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'
import ScratchcardTeaser from '#scratchcard/app/components/ScratchcardTeaser.vue'

type PlayNowScratchcardsProps = {
  loading?: boolean
  scratchcardTeasers?: (FreeToPlayTeaserPayload | ScratchcardTeaserPayload)[]
}

const { scratchcardTeasers = [] } = defineProps<PlayNowScratchcardsProps>()

const { isDesktop } = useDevice()

const priorityFetchAmount = computed(() => (isDesktop ? 6 : 3))

const TRACKING_LIST_NAME = 'Play Now Scratchcard Teasers'
const TRACKING_LIST_ID = 'play_now_scratchcard_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const { filterFreeToPlayTeasers, isFreeToPlayTeaser } = useFreeToPlay()

const teasers = computed(() => filterFreeToPlayTeasers(scratchcardTeasers))

const handleTeaserClick = (teaser: FreeToPlayTeaserPayload | ScratchcardTeaserPayload) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'ScratchcardTeaserPayload':
        return item.item_name === teaser.name
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(
  () => teasers.value,
  (list) => {
    if (!list || list.length === 0) return

    try {
      trackingTeaserListItems.value = createTeaserListItems(list)
      pushViewListEvent({
        items: trackingTeaserListItems.value,
        list_id: TRACKING_LIST_ID,
        list_name: TRACKING_LIST_NAME,
      })

      if (unwatchTeasers) unwatchTeasers()
    } catch (err) {
      logTrackingError('view_list', err)
    }
  },
)
</script>
